import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Pause } from "lucide-react";

export default function ProQuoteProfile() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ trades: [], communes: "", radius_km: 30, max_per_day: 0, max_per_month: 0, paused: false });

  useEffect(() => {
    api.get("/devis/pro/profile").then((r) => {
      setData(r.data);
      const p = r.data.profile || {};
      setForm({
        trades: p.trades || [], communes: (p.communes || []).join(", "),
        radius_km: p.radius_km || 30, max_per_day: p.max_per_day || 0,
        max_per_month: p.max_per_month || 0, paused: !!p.paused,
      });
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const toggleTrade = (slug) => setForm((f) => ({ ...f, trades: f.trades.includes(slug) ? f.trades.filter((t) => t !== slug) : [...f.trades, slug] }));

  const save = async () => {
    try {
      await api.post("/devis/pro/profile", {
        ...form, communes: form.communes.split(",").map((c) => c.trim()).filter(Boolean),
        radius_km: Number(form.radius_km), max_per_day: Number(form.max_per_day), max_per_month: Number(form.max_per_month),
      });
      toast.success("Profil enregistré.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-10 text-center text-slate-400"><Loader2 className="animate-spin inline" /> Chargement…</div>;
  const inputCls = "w-full h-11 px-3 rounded-xl border border-slate-200 outline-none focus:border-[#E05638] text-sm";

  return (
    <div className="max-w-2xl" data-testid="pro-quote-profile-page">
      <Link to="/pro/demandes-devis" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> Retour aux demandes</Link>
      <h1 className="font-heading text-2xl font-extrabold text-[#172A3A] mb-1">Mes métiers & zones d'intervention</h1>
      <p className="text-sm text-slate-500 mb-6">Précisez pour ne recevoir que les demandes qui vous correspondent. Laissez les métiers vides pour tout recevoir dans votre zone.</p>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div>
          <div className="text-sm font-semibold text-[#172A3A] mb-2">Métiers que vous proposez</div>
          <div className="flex flex-wrap gap-2">
            {(data.all_trades || []).map((t) => (
              <button key={t.slug} type="button" onClick={() => toggleTrade(t.slug)} data-testid={`pro-trade-${t.slug}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.trades.includes(t.slug) ? "border-[#E05638] bg-orange-50 text-[#E05638]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t.name}</button>
            ))}
          </div>
        </div>
        <label className="block"><span className="block text-sm font-semibold text-[#172A3A] mb-1.5">Communes d'intervention (séparées par des virgules)</span>
          <input className={inputCls} value={form.communes} onChange={(e) => setForm((f) => ({ ...f, communes: e.target.value }))} placeholder="Chartres, Lucé, Mainvilliers" data-testid="pro-communes" /></label>
        <label className="block"><span className="block text-sm font-semibold text-[#172A3A] mb-1.5">Rayon d'intervention : {form.radius_km} km</span>
          <input type="range" min="5" max="100" step="5" value={form.radius_km} onChange={(e) => setForm((f) => ({ ...f, radius_km: e.target.value }))} className="w-full accent-[#E05638]" data-testid="pro-radius" /></label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="block text-sm font-semibold text-[#172A3A] mb-1.5">Max demandes / jour (0 = illimité)</span>
            <input type="number" min="0" className={inputCls} value={form.max_per_day} onChange={(e) => setForm((f) => ({ ...f, max_per_day: e.target.value }))} data-testid="pro-max-day" /></label>
          <label className="block"><span className="block text-sm font-semibold text-[#172A3A] mb-1.5">Max demandes / mois (0 = illimité)</span>
            <input type="number" min="0" className={inputCls} value={form.max_per_month} onChange={(e) => setForm((f) => ({ ...f, max_per_month: e.target.value }))} data-testid="pro-max-month" /></label>
        </div>
        <label className="flex items-center gap-2.5 p-3 rounded-xl bg-[#F2F4F5] cursor-pointer" data-testid="pro-paused">
          <input type="checkbox" checked={form.paused} onChange={(e) => setForm((f) => ({ ...f, paused: e.target.checked }))} className="w-4 h-4 accent-[#E05638]" />
          <span className="text-sm text-slate-700 inline-flex items-center gap-1.5"><Pause size={14} /> Suspendre temporairement la réception de nouvelles demandes</span>
        </label>
        <button onClick={save} data-testid="pro-quote-profile-save" className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#E05638] hover:bg-[#c7482d]"><Save size={15} /> Enregistrer</button>
      </div>
    </div>
  );
}
